import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { TRIP_LEG_LABEL_KEY, type TripView } from '@/lib/transport/types';
import { providerLabel } from '@/lib/transport/providers';

// Cancel one ground-transport trip (feat/transport §7).
//
// Only ever opened for a NON-terminal trip — the card hides the control on the
// rest, because cancel is a no-op there by contract (brief §6.4) and offering a
// button that cannot change anything is worse than offering none. `reason` is
// required for the same audit reason as dispatch, and the BFF enforces it.
//
// Deliberately NOT a `window.confirm`: a native dialog blocks the whole tab and,
// per the browser-automation rules this console is verified under, wedges every
// subsequent interaction.

interface TransportCancelDialogProps {
  trip: TripView;
  /** Friendly passenger name (already masked) for the confirm copy. */
  passengerLabel: string;
  pending: boolean;
  error?: string | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void;
}

export function TransportCancelDialog({
  trip,
  passengerLabel,
  pending,
  error,
  onOpenChange,
  onConfirm,
}: TransportCancelDialogProps) {
  const { t } = useTranslation('pnrActions');
  const [reason, setReason] = useState('');
  const [validation, setValidation] = useState<string | null>(null);

  const submit = (): void => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setValidation(t('cancelTrip.noReason'));
      return;
    }
    setValidation(null);
    onConfirm(trimmed);
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent data-testid="transport-cancel-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="h-4 w-4" aria-hidden /> {t('cancelTrip.title')}
          </DialogTitle>
          <DialogDescription>
            {t('cancelTrip.descriptionPrefix', { leg: t(TRIP_LEG_LABEL_KEY[trip.leg]).toLowerCase() })}{' '}
            <span className="font-medium">{passengerLabel}</span>{' '}
            {t('cancelTrip.descriptionSuffix', { provider: providerLabel(trip.providerName) })}
          </DialogDescription>
        </DialogHeader>

        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('reason.requiredLabel')}</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder={t('cancelTrip.reasonPlaceholder')}
            className="w-full rounded-md border border-input bg-background p-2 text-sm"
            data-testid="transport-cancel-reason"
          />
        </label>

        {validation ? (
          <p className="text-xs text-destructive" role="alert" data-testid="transport-cancel-validation">
            {validation}
          </p>
        ) : null}
        {error ? (
          <p className="text-xs text-destructive" role="alert" data-testid="transport-cancel-error">
            {error}
          </p>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            {t('cancelTrip.keep')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={pending}
            onClick={submit}
            data-testid="transport-cancel-confirm"
          >
            {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
            {t('cancelTrip.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
